import { GLOBALS, resetMetrics, urijs } from '@wix/communities-blog-client-common';
import { blogAppDefId } from '../constants/apps';
import { type Router, setRouterMatch } from '../router';
import getEnvironment from '../services/get-environment';
import { getPathname } from '../store/location/get-pathname';
import { type AppStore } from '../types';
import { type PlatformApi } from './platform-api';

const REGEX_POST_PAGE_PREVIEW = /[0-9a-fA-F]+\/preview\/([^/]+)\/?/;

export const getPreviewInstance = (location: PlatformApi['location']) => {
  const match = location.path.join('/').match(REGEX_POST_PAGE_PREVIEW);
  return match ? match[1] : undefined;
};

export const getInstance = (platformApi: PlatformApi) => () =>
  platformApi.site.getAppToken?.(blogAppDefId);

const noop = () => {};

export const createLogger = (isDebug?: boolean, isProduction?: boolean) =>
  !isProduction || isDebug ? console.log : noop;

export const onLocationChange = ({
  platformApi,
  callback,
}: {
  platformApi: PlatformApi;
  callback: (args: { path: string[] }) => void;
}) => {
  if (!platformApi.location.onChange) {
    return;
  }
  let currentPathname = platformApi.location.path.join('/');

  platformApi.location.onChange((args) => {
    const pathname = args.path.join('/');
    if (pathname === currentPathname) {
      return;
    }
    currentPathname = pathname;
    callback(args);
  });
};

export const doRouting = ({
  router,
  pathname,
  store,
  isInitialLoad = true,
}: {
  router: Router;
  pathname: string;
  store: AppStore;
  isInitialLoad?: boolean;
}) =>
  isInitialLoad
    ? router.resolve(pathname).then((match) => store.dispatch(setRouterMatch(match)))
    : router.match(pathname);

/**
 * Prefixes `baseUrl` with the site origin during SSR. Node has no document origin, so relative
 * `/_api/...` paths would otherwise hit the wrong host.
 *
 * Absolute URLs skip Yoshi `httpClient`'s automatic instance injection. Always pass
 * {@link appendAuthHeaders} on the same request, or the API returns 403 `InstanceMissingError`.
 */
export const appendOriginInBackend = ({
  platformApi,
  baseUrl,
}: {
  platformApi: PlatformApi;
  baseUrl: string;
}) => {
  const origin = urijs(platformApi.location.baseUrl).origin();
  const isBackend = platformApi.window.rendering.env === 'backend';
  return isBackend ? `${origin}${baseUrl}` : baseUrl;
};

/** Instance `Authorization` header. Required whenever {@link appendOriginInBackend} (or another absolute URL) is used. */
export const appendAuthHeaders = (
  platformApi: PlatformApi,
  headers: Record<string, string | undefined> = {},
) => ({
  ...headers,
  Authorization: getInstance(platformApi)(),
});

export const getBlogApiBaseUrl = (platformApi: PlatformApi) => {
  const { isEditor, isPreview } = getEnvironment(platformApi);
  return isEditor || isPreview
    ? GLOBALS.API_BASE_URL_CLIENT
    : `${platformApi.location.baseUrl}${GLOBALS.API_BASE_URL_CLIENT}`;
};

export const createControllerId = () => new Date().getTime();

export const getSectionPathname = ({
  platformApi,
  sectionUrl,
}: {
  platformApi: PlatformApi;
  sectionUrl: string | undefined;
}) => {
  return getPathname(platformApi.location, sectionUrl);
};

export const handleLocationChange = ({
  platformApi,
  store,
  log,
  bundleName,
  router,
  sectionUrl,
}: {
  platformApi: PlatformApi;
  store: AppStore;
  log: (...args: unknown[]) => void;
  bundleName: 'post-page' | 'blog';
  router: Router;
  sectionUrl: string | undefined;
}) => {
  onLocationChange({
    platformApi,
    callback: ({ path }) => {
      log('navigated to new path', path);
      resetMetrics(bundleName);
      doRouting({
        store,
        router,
        pathname: getSectionPathname({ platformApi, sectionUrl }),
        isInitialLoad: false,
      });
    },
  });
};
